import type { MappingTreeStatus } from "../domain/mappingTree";
import { Icon, cx } from "./ui";

export type MappingStatusMarkProps = {
  readonly status: MappingTreeStatus;
  readonly size?: "sm" | "md";
};

export const mappingStatusMarkLabel = (status: MappingTreeStatus): string => ({
  approved: "Mapped and verified",
  unapproved: "Mapped and requires attention",
  mixed: "Contains mixed coverage or review states",
  unmapped: "No accepted mapping",
})[status];

export const mappingStatusMarkToneClass = (status: MappingTreeStatus): string => ({
  approved: "mapping-status-approved bg-emerald-600 text-white ring-emerald-700",
  unapproved: "mapping-status-unapproved bg-amber-500 text-white ring-amber-600",
  mixed: "mapping-status-mixed text-white ring-slate-400",
  unmapped: "mapping-status-unmapped bg-white text-rose-500 ring-rose-300",
})[status];

export function MappingStatusMark({ status, size = "sm" }: MappingStatusMarkProps) {
  const label = mappingStatusMarkLabel(status);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-status={status}
      className={cx(
        "relative inline-grid shrink-0 place-items-center overflow-hidden rounded-[3px] ring-1",
        size === "sm" ? "h-4 w-4" : "h-[18px] w-[18px]",
        mappingStatusMarkToneClass(status),
      )}
      style={status === "mixed" ? { background: "linear-gradient(90deg, #059669 0 50%, #f59e0b 50% 100%)" } : undefined}
    >
      {status === "approved" && <Icon name="check" size={size === "sm" ? 10 : 11} stroke={2.6} />}
      {status === "unapproved" && <span className="h-1.5 w-1.5 rounded-full border-2 border-white" aria-hidden="true" />}
      {status === "mixed" && <span className="h-1.5 w-1.5 rounded-full bg-white ring-1 ring-slate-500/20" aria-hidden="true" />}
      {status === "unmapped" && <span className="h-0.5 w-1.5 rounded-full bg-rose-400" aria-hidden="true" />}
    </span>
  );
}
